import * as Highcharts from 'highcharts';
import * as numeral from 'numeral';
import * as _ from 'underscore';

import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { ColorPalette } from '../../../analytics-core/interfaces/partner.interface';
import { ColorConfig, DownloadConfig } from '../../../analytics-core/interfaces/widget.interface';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { EVENT_EMITTERS } from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';

@Component({
	selector: 'app-daypart-donut',
	styleUrls: ['./daypart-donut.component.css'],
	templateUrl: './daypart-donut.component.html'
})
export class DaypartDonutComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('chartElement', { static: true }) chartElement: ElementRef;
	@Input() colorsconfig: any;
	@Input() downloadconfig: any;
	@Input() key: string;
	@Input() sectiontitle: string;
	@Input() chartconfiguration: any;

	public downloadConfiguration: DownloadConfig;
	public colorPalette: ColorPalette;
	public reloadSubscription: any;
	public widgetDataSubscription: any;
	public chartConfig: any;
	public chartObj: any;
	public timezone: string = '';

	public daypartChartObjArray: any[] = [];
	public showLoad = true;
	public hasData = true;
	public displayOrder = {
		'After Peak': 4,
		'Before Peak': 2,
		'Off Peak': 1,
		Peak: 3,
		'Unknown Peak': 5
	};

	constructor(
		protected analyticsService: AnalyticsService,
		protected colorUtilityService: ColorUtilityService,
		protected analyticsDataModel: AnalyticsDataModel
	) {
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe((response: any) => {
			this.requestData();
		});
	}

	ngOnInit(): void {
		this.chartConfig = JSON.parse(decodeURI(this.chartconfiguration));
		this.colorsconfig = JSON.parse(decodeURI(this.colorsconfig));
		this.downloadConfiguration = JSON.parse(decodeURI(this.downloadconfig));
		this.colorPalette = this.analyticsDataModel.partner.colorPalette;
	}

	ngAfterViewInit(): void {
		this.requestData();
	}

	ngOnDestroy(): void {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
	}

	requestData(): void {
		this.hasData = true;
		this.showLoad = true;
		this.widgetDataSubscription = this.analyticsService
			.processCustomServiceRequest(this.key)
			.subscribe((response: any) => {
				this.showLoad = false;
				this.timezone = response.timezone ? `(${response.timezone})` : '';
				if (response.success && response.data.length > 0) {
					this.hasData = true;
					this.processResponseData(response.data);
				} else {
					this.hasData = false;
				}
			});
	}

	processResponseData(responseData: any): void {
		this.daypartChartObjArray = this.buildDayPartChartCollatorByFields(responseData);
		this.daypartChartObjArray[0].chartObj.map((data, index) => {
			if (data.dayparttime) {
				data.color = this.getColorFromColorConfig(index, 1);
			}
		});
		this.drawDonutChart();
	}

	buildDayPartChartCollatorByFields(daypartResponseData: any): any[] {
		const fieldChartArray: any[] = [];
		const chartObjArray: any[] = [];
		const daypartData: any = {};
		for (let index = 0; index < daypartResponseData.length; index++) {
			const chartObj: any = {};
			chartObj.name = daypartResponseData[index].daypart;
			chartObj.dayparttime = daypartResponseData[index].friendly_name;
			chartObj.y = Number(daypartResponseData[index].impressions);
			// chartObj.displayOrder = (this.displayOrder[daypartResponseData[i].daypart]) ? this.displayOrder[daypartResponseData[i].daypart] : 9;
			chartObj.displayOrder = daypartResponseData[index].display_order || index;
			chartObjArray.push(chartObj);
		}
		daypartData.chartObj = _.sortBy(chartObjArray, 'displayOrder');
		fieldChartArray.push(daypartData);
		return fieldChartArray;
	}

	getColorFromColorConfig(index: number, opacity?: number): string {
		const colorConfig: ColorConfig = this.colorsconfig[index];

		const fillOpacity = colorConfig ? colorConfig.opacity : opacity;
		let rgb = '';
		if (colorConfig && colorConfig.isHex) {
			rgb = this.colorUtilityService.getRGBByOpacity(colorConfig.color, fillOpacity);
		} else if (colorConfig && colorConfig.color) {
			rgb = this.colorUtilityService.getRGBByColorType(this.colorPalette, colorConfig.color, fillOpacity);
		}
		return rgb;
	}

	drawDonutChart(): void {
		this.chartObj = Highcharts.chart('chartElement', {
			chart: {
				margin: this.chartConfig.graphConfig.margin
			},
			credits: {
				enabled: false
			},
			legend: {
				align: this.chartConfig.graphConfig.legendConfig.align,
				itemDistance: this.chartConfig.graphConfig.legendConfig.itemDistance,
				itemHoverStyle: {
					color: this.chartConfig.graphConfig.legendConfig.itemHoverStyle.color,
					fontWeight: this.chartConfig.graphConfig.legendConfig.itemHoverStyle.fontWeight
				},
				itemMarginBottom: this.chartConfig.graphConfig.legendConfig.itemMarginBottom,
				itemStyle: {
					color: this.chartConfig.graphConfig.legendConfig.itemStyle.color,
					fontWeight: this.chartConfig.graphConfig.legendConfig.itemStyle.fontWeight
				},
				itemWidth: this.chartConfig.graphConfig.legendConfig.itemWidth,
				verticalAlign: this.chartConfig.graphConfig.legendConfig.verticalAlign,
				x: this.chartConfig.graphConfig.legendConfig.x,
				y: this.chartConfig.graphConfig.legendConfig.y,
				labelFormatter(): string {
					return this.name;
				}
			},
			plotOptions: {
				pie: {
					borderWidth: 0
				}
			},
			series: [
				{
					data: this.daypartChartObjArray[0].chartObj,
					dataLabels: {
						enabled: false
					},
					innerSize: this.chartConfig.seriesConfig[0].innerSize,
					showInLegend: true,
					size: this.chartConfig.seriesConfig[0].size,
					states: {
						hover: {
							enabled: false
						},
						inactive: {
							opacity: 1
						}
					},
					type: this.chartConfig.seriesConfig[0].type
				}
			],
			title: {
				text: null
			},
			tooltip: {
				backgroundColor: this.chartConfig.seriesConfig[0].tooltipInfo[0].backgroundColor,
				borderWidth: this.chartConfig.seriesConfig[0].tooltipInfo[0].borderWidth,
				padding: this.chartConfig.seriesConfig[0].tooltipInfo[0].padding,
				shadow: this.chartConfig.seriesConfig[0].tooltipInfo[0].shadow,
				style: {
					color: this.chartConfig.seriesConfig[0].tooltipInfo[0].style.color
				},
				useHTML: true,
				formatter(): string {
					return `<div style="text-align: center; font-size: 14px">
						<div style="padding-bottom: 5px;">${this.point['dayparttime']}</div>
						<div>${numeral(this.y).format('0,0')} impressions</div>
						</div>
					`;
				}
				// followPointer: false
			}
		});
	}
}
